import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Eye, EyeOff, Loader2, LockKeyholeIcon } from "lucide-react";
import { useEffect, useState, type HTMLInputAutoCompleteAttribute } from "react";
import { type Control, type FieldPath, type FieldValues, useForm } from "react-hook-form";
import { z } from "zod";

import type { FormSchema } from "@/lib/schema";
import type { InputWithIconProps } from "@/types";

import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { FlatInputOTPSlot, InputOTP, InputOTPGroup } from "../ui/input-otp";

interface FormFieldProps {
	control: Control<FormSchema>;
	name: FieldPath<FormSchema>;
	placeholder?: string;
	label?: string;
	options?: string[];
}

export const OTPSchema = z.object({
	pin: z.string().min(6, {
		message: "Your one-time password must be 6 digits."
	})
});

function getAutoComplete(name: string, type?: string): HTMLInputAutoCompleteAttribute {
	if (name === "email" || type === "email")
		return "email";
	if (name === "confirmPassword")
		return "new-password";
	if (name === "password" || type === "password") {
		if (typeof location !== "undefined" && location.pathname.includes("sign-in"))
			return "current-password";
		return "new-password";
	}
	return "on";
}

export function CustomFormField<T extends FieldValues>({
	className,
	control,
	name,
	type,
	placeholder,
	Icon,
	autoComplete,
	...props
}: InputWithIconProps<T>) {
	const [isVisible, setIsVisible] = useState(false);
	const toggleVisibility = () => setIsVisible(!isVisible);

	const isPasswordField = type === "password";
	return (
		<FormField
			control={control}
			name={name}
			render={({ field, fieldState: { error } }) => (
				<FormItem className="form-item">
					<FormControl>
						<>
							<div className="relative w-full">
								<Input
									id={name}
									{...field}
									{...props}
									type={
										isPasswordField ? (isVisible ? "text" : "password") : type
									}
									value={(field.value as string) || ""}
									onChange={field.onChange}
									placeholder={placeholder}
									autoComplete={autoComplete ?? getAutoComplete(String(name), type)}
									className={cn(
										"peer pl-9 shadow-none border-primary",
										error
										&& "border-destructive bg-destructive/10 focus-visible:ring-destructive",
										className
									)}
								/>
								{
									Icon
									&& (
										<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center pl-3 text-muted-foreground/80 peer-disabled:opacity-50">
											<Icon
												color="#3F2EC8"
												size={20}
												strokeWidth={2}
												aria-hidden="true"
												role="presentation"
											/>
										</div>
									)

								}
								{isPasswordField && (
									<button
										className="absolute inset-y-px right-px flex h-full w-9 items-center justify-center rounded-r-lg text-muted-foreground/80 transition-shadow hover:text-foreground focus-visible:border focus-visible:border-ring focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
										type="button"
										onClick={toggleVisibility}
										aria-label={isVisible ? "Hide password" : "Show password"}
										aria-pressed={isVisible}
										aria-controls="password"
									>
										{isVisible
											? (
													<EyeOff
														color="#3F2EC8"
														size={20}
														strokeWidth={2}
														aria-hidden="true"
														role="presentation"
													/>
												)
											: (
													<Eye
														color="#3F2EC8"
														size={20}
														strokeWidth={2}
														aria-hidden="true"
														role="presentation"
													/>
												)}
									</button>
								)}
							</div>
						</>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}

export function PasswordFields<T extends FieldValues>({
	control,
	showConfirm
}: {

	control: Control<T>;
	showConfirm: boolean;
}) {
	return (
		<>
			<CustomFormField<T>
				control={control}
				placeholder={`Enter ${location.pathname.includes("forgot-password") ? "new " : ""}password`}
				className="h-14"
				type="password"
				Icon={LockKeyholeIcon}
				name={"password" as FieldPath<T>}
			/>
			{showConfirm && (
				<CustomFormField
					control={control}
					placeholder={`Confirm ${location.pathname.includes("forgot-password") ? "new " : ""}password`}
					className="h-14"
					type="password"
					Icon={LockKeyholeIcon}
					name={"confirmPassword" as FieldPath<T>}
				/>
			)}
		</>
	);
}

export function CustomCheckbox({ control, name, label }: FormFieldProps) {
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<>
					<FormItem className="flex place-items-center justify-start gap-2">
						<FormControl>
							<Checkbox
								aria-label={label}
								className="mt-2"
								checked={field.value as boolean}
								onCheckedChange={field.onChange}
							/>
						</FormControl>
						<FormLabel htmlFor="tos" className=" text-sm">
							By signing up, you agree to the
							{" "}
							<a target="_blank" href="/terms" className="text-primary">
								Terms of Service
							</a>
							{" "}
							and
							{" "}
							<a target="_blank" href="/privacy" className="text-primary">
								Privacy Policy
							</a>
						</FormLabel>
					</FormItem>
					<FormMessage className="m-0 p-0" />
				</>
			)}
		/>
	);
}

const RESEND_DELAY = 60;
export function InputOTPForm({
	onSubmit,
	isPending,
	handleResend
}: {
	isPending: boolean;
	handleResend: () => void;
	onSubmit: (values: z.infer<typeof OTPSchema>) => void;
}) {
	const [countdown, setCountdown] = useState(RESEND_DELAY);
	const [canResend, setCanResend] = useState(false);

	const form = useForm<z.infer<typeof OTPSchema>>({
		resolver: zodResolver(OTPSchema),
		defaultValues: {
			pin: ""
		}
	});

	useEffect(() => {
		const timer = setInterval(() => {
			setCountdown(prev => prev - 1);
		}, 1000);
		if (countdown === 0) {
			clearInterval(timer);
			setCanResend(true);
			return;
		}
		return () => clearInterval(timer);
	}, [countdown]);

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="pin"
					render={({ field }) => (
						<FormItem className="border-0">
							<FormControl>
								<InputOTP maxLength={6} {...field} pattern={REGEXP_ONLY_DIGITS}>
									<InputOTPGroup className="w-full justify-between p-0">
										<FlatInputOTPSlot index={0} />
										<FlatInputOTPSlot index={1} />
										<FlatInputOTPSlot index={2} />
										<FlatInputOTPSlot index={3} />
										<FlatInputOTPSlot index={4} />
										<FlatInputOTPSlot index={5} />
									</InputOTPGroup>
								</InputOTP>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<div>
					Didn't Receive any code?
					{" "}
					<Button
						disabled={!canResend}
						type="button"
						variant="link"
						className="inline-flex p-0 hover:no-underline"
						onClick={() => {
							form.reset();
							form.clearErrors();
							handleResend();
							setCanResend(false);
							setCountdown(RESEND_DELAY);
						}}
					>
						Resend
						{" "}
						{countdown === 0 ? "now" : `in ${countdown}s`}
					</Button>
				</div>

				<Button disabled={isPending} type="submit" className="h-14 w-full">
					{
						isPending

							? (
									<>
										<Loader2 size={20} className="animate-spin" />
										{" "}
										&nbsp;
										Loading....
									</>
								)
							: "Submit"
					}
				</Button>
			</form>
		</Form>
	);
}

export function SelectForm({
	control,
	name,
	options,
	placeholder
}: FormFieldProps) {
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<Select onValueChange={field.onChange}>
						<FormControl className="h-14 border-primary">
							<SelectTrigger aria-label={placeholder}>
								<SelectValue
									className="font-bold capitalize"
									placeholder={placeholder}
								/>
							</SelectTrigger>
						</FormControl>
						<SelectContent>
							{options?.map(option => (
								<SelectItem
									className="capitalize"
									key={crypto.randomUUID()}
									value={option.toUpperCase()}
								>
									{option}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}
